import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Maximize2, Minimize2 } from 'lucide-react';
import type { Product, Flyer, FlyerItem } from '@shared/schema';

type LayoutStyle = 'classic' | 'flyer';

// Flyer item with product data and optional override price
interface FlyerItemWithProduct extends FlyerItem {
  product: Product;
}

// Current flyer response from API
interface CurrentFlyerResponse extends Flyer {
  items: FlyerItemWithProduct[];
}

// Get fallback image based on description
const getFallbackImage = (description: string): string => {
  const desc = description.toLowerCase();
  if (desc.includes('beef') || desc.includes('striploin') || desc.includes('short rib'))
    return 'https://images.unsplash.com/photo-1588347818036-558601350947?w=400&h=300&fit=crop';
  if (desc.includes('pork'))
    return 'https://images.unsplash.com/photo-1641558478751-ad448f3c79fe?w=400&h=300&fit=crop';
  if (desc.includes('chicken'))
    return 'https://images.unsplash.com/photo-1587593810167-a84920ea0781?w=400&h=300&fit=crop';
  if (desc.includes('fish') || desc.includes('seafood') || desc.includes('salmon'))
    return 'https://images.unsplash.com/photo-1544943910-4c1dc44aab44?w=400&h=300&fit=crop';
  return 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&h=300&fit=crop';
};

// ============ CLASSIC LAYOUT COMPONENTS ============

function ClassicProductCard({ product }: { product: Product }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const savingsPercent = product.retailPrice > product.ourPrice
    ? Math.round(((product.retailPrice - product.ourPrice) / product.retailPrice) * 100)
    : 0;

  return (
    <div className="bg-white overflow-hidden h-full flex flex-col border border-gray-400">
      <div className="flex flex-1">
        {product.chineseName && (
          <div className="w-10 flex-shrink-0 flex items-center justify-center bg-gradient-to-b from-emerald-100 to-emerald-200">
            <div
              className="font-bold text-emerald-800 text-xl"
              style={{ writingMode: 'vertical-rl', textOrientation: 'upright', letterSpacing: '0.02em' }}
            >
              {product.chineseName}
            </div>
          </div>
        )}
        <div className="flex-1 relative bg-gray-100">
          {imageLoading && !imageError && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
          <img
            src={product.imageUrl || getFallbackImage(product.description)}
            alt={product.description}
            onError={() => { setImageError(true); setImageLoading(false); }}
            onLoad={() => setImageLoading(false)}
            className={`w-full h-full object-cover ${imageLoading ? 'invisible' : 'visible'}`}
          />
          {imageError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <span className="text-3xl opacity-50">🛒</span>
            </div>
          )}
          {savingsPercent > 0 && (
            <div className="absolute top-1 left-1 bg-red-600 text-white font-bold rounded shadow text-sm px-2 py-0.5">
              {savingsPercent}% OFF
            </div>
          )}
        </div>
      </div>
      <div className="p-2 flex gap-2">
        <div className="flex-1 flex flex-col justify-center min-w-0">
          <h3 className="font-bold text-gray-900 leading-tight line-clamp-2 text-sm">{product.description}</h3>
          <span className="text-red-500 line-through text-sm">${product.retailPrice.toFixed(2)}</span>
        </div>
        <div className="flex items-center flex-shrink-0">
          <span className="font-black text-emerald-600 text-4xl leading-none">${product.ourPrice.toFixed(2)}</span>
          <span className="text-gray-500 uppercase text-xs ml-1 self-end mb-1">{product.size}</span>
        </div>
      </div>
    </div>
  );
}

function ClassicEmptySlot() {
  return (
    <div className="bg-gray-100 border border-gray-300 h-full flex items-center justify-center">
      <div className="text-gray-300 text-4xl opacity-30">🛒</div>
    </div>
  );
}

// Classic layout category colors
const CLASSIC_CATEGORY_COLORS: Record<string, { bg: string; headerBg: string; headerText: string; border: string }> = {
  'produce': { bg: 'bg-emerald-50', headerBg: 'bg-emerald-600', headerText: 'text-white', border: 'border-emerald-600' },
  'meat': { bg: 'bg-red-50', headerBg: 'bg-red-600', headerText: 'text-white', border: 'border-red-600' },
  'seafood': { bg: 'bg-blue-50', headerBg: 'bg-blue-600', headerText: 'text-white', border: 'border-blue-600' },
  'groceries': { bg: 'bg-amber-50', headerBg: 'bg-amber-500', headerText: 'text-white', border: 'border-amber-500' },
  'grocery': { bg: 'bg-amber-50', headerBg: 'bg-amber-500', headerText: 'text-white', border: 'border-amber-500' },
  'default': { bg: 'bg-gray-50', headerBg: 'bg-gray-600', headerText: 'text-white', border: 'border-gray-600' }
};

const getClassicCategoryColors = (title: string) => {
  const key = title.toLowerCase();
  return CLASSIC_CATEGORY_COLORS[key] || CLASSIC_CATEGORY_COLORS.default;
};

function ClassicCategorySection({ title, products, columns, rows }: { title: string; products: Product[]; columns: number; rows: number }) {
  const colors = getClassicCategoryColors(title);
  const totalSlots = columns * rows;
  const displayProducts = products.slice(0, totalSlots);
  const emptySlots = totalSlots - displayProducts.length;

  return (
    <div className={`flex flex-col h-full ${colors.bg}`}>
      <h2 className={`font-bold ${colors.headerText} ${colors.headerBg} px-2 py-0.5 text-base flex-shrink-0`}>{title}</h2>
      <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`, gap: '0px' }}>
        {displayProducts.map((product) => <ClassicProductCard key={product.id} product={product} />)}
        {/* Fill remaining slots with empty placeholders */}
        {Array.from({ length: emptySlots }).map((_, i) => <ClassicEmptySlot key={`empty-${i}`} />)}
      </div>
    </div>
  );
}

// ============ FLYER LAYOUT COMPONENTS ============

const CATEGORY_COLORS: Record<string, { bg: string; headerBg: string }> = {
  'produce': { bg: 'bg-emerald-50', headerBg: 'bg-emerald-600' },
  'meat': { bg: 'bg-red-50', headerBg: 'bg-red-600' },
  'seafood': { bg: 'bg-blue-50', headerBg: 'bg-blue-600' },
  'grocery': { bg: 'bg-amber-50', headerBg: 'bg-amber-500' },
  'groceries': { bg: 'bg-amber-50', headerBg: 'bg-amber-500' },
  'frozen': { bg: 'bg-cyan-50', headerBg: 'bg-cyan-600' },
  'bakery': { bg: 'bg-orange-50', headerBg: 'bg-orange-500' },
  'dairy': { bg: 'bg-yellow-50', headerBg: 'bg-yellow-500' },
  'beverages': { bg: 'bg-purple-50', headerBg: 'bg-purple-600' },
  'snacks': { bg: 'bg-pink-50', headerBg: 'bg-pink-500' },
  'household': { bg: 'bg-slate-50', headerBg: 'bg-slate-600' },
  'default': { bg: 'bg-gray-50', headerBg: 'bg-gray-600' }
};

const getCategoryColors = (category: string) => {
  const cat = category.toLowerCase();
  for (const [key, colors] of Object.entries(CATEGORY_COLORS)) {
    if (cat.includes(key)) return colors;
  }
  return CATEGORY_COLORS.default;
};

function FlyerProductCard({ product }: { product: Product }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const savingsPercent = product.retailPrice > product.ourPrice
    ? Math.round(((product.retailPrice - product.ourPrice) / product.retailPrice) * 100) : 0;

  return (
    <div className="bg-white border border-gray-300 h-full flex overflow-hidden relative">
      {savingsPercent > 0 && (
        <div className="absolute top-1 right-1 bg-red-600 text-white text-xs font-bold px-1.5 py-0.5 rounded z-10">
          {savingsPercent}% OFF
        </div>
      )}
      {/* Vertical Chinese */}
      {product.chineseName && (
        <div className="w-8 flex-shrink-0 bg-gradient-to-b from-emerald-500 to-emerald-600 flex items-center justify-center">
          <div
            className="text-black text-base font-black"
            style={{ writingMode: 'vertical-rl', textOrientation: 'upright', letterSpacing: '0.05em', textShadow: '0 0 2px rgba(255,255,255,0.8)' }}
          >
            {product.chineseName.slice(0, 5)}
          </div>
        </div>
      )}
      {/* Image */}
      <div className="w-24 flex-shrink-0 relative bg-gray-100">
        {imageLoading && !imageError && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
        {product.imageUrl && !imageError ? (
          <img src={product.imageUrl} alt={product.description} onError={() => setImageError(true)} onLoad={() => setImageLoading(false)}
            className={`w-full h-full object-cover ${imageLoading ? 'invisible' : 'visible'}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200"><span className="text-2xl opacity-30">🛒</span></div>
        )}
      </div>
      {/* Content */}
      <div className="flex-1 p-1.5 flex flex-col justify-center min-w-0 overflow-hidden">
        {/* Description */}
        <p className="text-xs font-bold text-gray-800 leading-tight line-clamp-2 mb-0.5">{product.description}</p>
        {/* Price - ensure no clipping */}
        <div className="flex items-baseline gap-1 flex-wrap">
          <span className="text-2xl font-black text-emerald-600 leading-none whitespace-nowrap">${(product.ourPrice || 0).toFixed(2)}</span>
          <span className="text-xs text-gray-500 uppercase truncate">{product.size}</span>
        </div>
        {savingsPercent > 0 && (
          <span className="text-xs text-gray-400 line-through">${product.retailPrice.toFixed(2)}</span>
        )}
      </div>
    </div>
  );
}

function FlyerEmptySlot() {
  return (
    <div className="bg-white/50 border border-gray-200 h-full flex items-center justify-center">
      <div className="text-gray-300 text-4xl opacity-30">🛒</div>
    </div>
  );
}

function FlyerCategorySection({ title, products, columns, rows }: { title: string; products: Product[]; columns: number; rows: number }) {
  const colors = getCategoryColors(title);
  const totalSlots = columns * rows;
  const displayProducts = products.slice(0, totalSlots);
  const emptySlots = totalSlots - displayProducts.length;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className={`${colors.headerBg} px-3 py-1 flex-shrink-0`}>
        <h3 className="text-white font-bold text-sm uppercase tracking-wide">{title}</h3>
      </div>
      <div className={`flex-1 grid ${colors.bg}`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`, gap: '1px' }}>
        {displayProducts.map((product) => <FlyerProductCard key={product.id} product={product} />)}
        {/* Fill remaining slots with empty placeholders */}
        {Array.from({ length: emptySlots }).map((_, i) => <FlyerEmptySlot key={`empty-${i}`} />)}
      </div>
    </div>
  );
}

function PromoBanner({ isFullscreen }: { isFullscreen: boolean }) {
  return (
    <div className="relative overflow-hidden flex-shrink-0" style={{ height: isFullscreen ? '10vh' : '120px', background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 50%, #7f1d1d 100%)' }}>
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
      {/* Logo - Left */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2 z-10">
        <img src="/images/TopsLogoCrop.png" alt="TOPS" className={`${isFullscreen ? 'h-16' : 'h-14'} w-auto drop-shadow-lg`} />
      </div>
      {/* Title - Centered */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center z-10">
        <div className={`${isFullscreen ? 'text-4xl' : 'text-2xl'} font-black text-white drop-shadow-lg tracking-wide`}>WEEKLY SPECIALS</div>
        <div className={`${isFullscreen ? 'text-lg' : 'text-sm'} text-yellow-300 font-bold`}>Fresh Deals Every Day!</div>
      </div>
      {/* Right side info */}
      <div className="absolute right-6 top-1/2 -translate-y-1/2 text-right z-10">
        <div className="bg-yellow-400 text-red-800 px-3 py-1 rounded-lg font-black text-sm mb-1 shadow-lg">THIS WEEK ONLY!</div>
        <div className="text-white/90 text-xs">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
        <div className="text-yellow-300 text-xs font-bold">www.topssupermarket.ca</div>
      </div>
    </div>
  );
}

// Product with optional flyer price override
interface DisplayProduct extends Product {
  displayPrice?: number; // Flyer price if set, otherwise ourPrice
}

// ============ MAIN COMPONENT ============

export default function InstoreDisplay() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [layoutStyle, setLayoutStyle] = useState<LayoutStyle>('classic');
  const [currentTime, setCurrentTime] = useState(new Date());
  const containerRef = useRef<HTMLDivElement>(null);

  // Update clock every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Auto-reload page every 30 minutes to prevent memory issues on long-running displays
  useEffect(() => {
    const reloadTimer = setTimeout(() => {
      window.location.reload();
    }, 30 * 60 * 1000); // 30 minutes
    return () => clearTimeout(reloadTimer);
  }, []);

  // Set page title
  useEffect(() => {
    document.title = 'Weekly Specials - Tops100';
  }, []);

  // Try to fetch current published flyer
  const { data: currentFlyer, isLoading: flyerLoading } = useQuery({
    queryKey: ['current-flyer'],
    queryFn: async () => {
      const flyer = await apiRequest<CurrentFlyerResponse | null>('/api/flyers/current');
      return flyer;
    },
    refetchInterval: 60000, // Refresh data every minute
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  // Fallback: fetch products with instoreFlyer flag (legacy mode)
  const { data: legacyProducts = [], isLoading: legacyLoading } = useQuery({
    queryKey: ['instore-flyer-products-legacy'],
    queryFn: async () => {
      const allProducts = await apiRequest<Product[]>('/api/products');
      return allProducts.filter(p => p.active && p.instoreFlyer);
    },
    refetchInterval: 60000,
    staleTime: 30000,
    enabled: !currentFlyer // Only fetch if no published flyer
  });

  const isLoading = flyerLoading || (legacyLoading && !currentFlyer);

  // Determine display mode and products
  const { usingFlyer, products, flyerTitle } = useMemo(() => {
    if (currentFlyer && currentFlyer.items && currentFlyer.items.length > 0) {
      // Using published flyer - convert items to display products
      const displayProducts: DisplayProduct[] = currentFlyer.items.map(item => ({
        ...item.product,
        displayPrice: item.flyerPrice ?? item.product.ourPrice, // Use flyer price if set
        ourPrice: item.flyerPrice ?? item.product.ourPrice, // Override ourPrice for display
      }));
      return { usingFlyer: true, products: displayProducts, flyerTitle: currentFlyer.title };
    } else {
      // Legacy mode - use instoreFlyer flagged products
      return { usingFlyer: false, products: legacyProducts as DisplayProduct[], flyerTitle: null };
    }
  }, [currentFlyer, legacyProducts]);

  // Group products by section
  const { produceProducts, meatProducts, groceryProducts } = useMemo(() => {
    if (usingFlyer && currentFlyer?.items) {
      // When using flyer, group by section field
      const produce: DisplayProduct[] = [];
      const meat: DisplayProduct[] = [];
      const grocery: DisplayProduct[] = [];

      currentFlyer.items.forEach(item => {
        const displayProduct: DisplayProduct = {
          ...item.product,
          displayPrice: item.flyerPrice ?? item.product.ourPrice,
          ourPrice: item.flyerPrice ?? item.product.ourPrice,
        };

        if (item.section === 'produce') produce.push(displayProduct);
        else if (item.section === 'meat') meat.push(displayProduct);
        else if (item.section === 'grocery') grocery.push(displayProduct);
      });

      // Sort by displayOrder within each section
      const sortByOrder = (items: FlyerItemWithProduct[], section: string) =>
        items.filter(i => i.section === section).sort((a, b) => a.displayOrder - b.displayOrder);

      const sortedProduce = sortByOrder(currentFlyer.items, 'produce');
      const sortedMeat = sortByOrder(currentFlyer.items, 'meat');
      const sortedGrocery = sortByOrder(currentFlyer.items, 'grocery');

      return {
        produceProducts: sortedProduce.map(item => ({
          ...item.product,
          ourPrice: item.flyerPrice ?? item.product.ourPrice,
        })) as DisplayProduct[],
        meatProducts: sortedMeat.map(item => ({
          ...item.product,
          ourPrice: item.flyerPrice ?? item.product.ourPrice,
        })) as DisplayProduct[],
        groceryProducts: sortedGrocery.map(item => ({
          ...item.product,
          ourPrice: item.flyerPrice ?? item.product.ourPrice,
        })) as DisplayProduct[],
      };
    } else {
      // Legacy mode - group by category
      const produce: DisplayProduct[] = [], meat: DisplayProduct[] = [], grocery: DisplayProduct[] = [];
      products.forEach(product => {
        const cat = (product.category || '').toLowerCase();
        if (cat.includes('produce') || cat.includes('fruit') || cat.includes('vegetable')) produce.push(product);
        else if (cat.includes('meat') || cat.includes('beef') || cat.includes('pork') || cat.includes('chicken') || cat.includes('poultry') || cat.includes('seafood') || cat.includes('fish')) meat.push(product);
        else grocery.push(product);
      });
      return { produceProducts: produce, meatProducts: meat, groceryProducts: grocery };
    }
  }, [products, usingFlyer, currentFlyer]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) { containerRef.current?.requestFullscreen(); setIsFullscreen(true); }
    else { document.exitFullscreen(); setIsFullscreen(false); }
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  if (isLoading) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="text-3xl text-gray-600">Loading...</div></div>;
  }

  return (
    <div ref={containerRef} className="min-h-screen bg-gray-100">
      {/* Control Bar */}
      {!isFullscreen && (
        <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-lg font-medium text-gray-700">In-Store TV Display ({products.length} items)</span>
            {usingFlyer ? (
              <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-sm font-medium">
                Using Flyer: {flyerTitle}
              </span>
            ) : (
              <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded text-sm font-medium">
                Legacy Mode (Weekly Toggle)
              </span>
            )}

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Layout:</label>
              <select value={layoutStyle} onChange={(e) => setLayoutStyle(e.target.value as LayoutStyle)} className="border border-gray-300 rounded px-2 py-1 text-sm font-medium">
                <option value="classic">Classic</option>
                <option value="flyer">Flyer Style</option>
              </select>
            </div>
          </div>
          <Button onClick={toggleFullscreen} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6">
            <Maximize2 className="w-4 h-4 mr-2" />Fullscreen for TV
          </Button>
        </div>
      )}

      {/* Main Display - 16:9 aspect ratio for TV (1080p/4K) */}
      <div className={`${isFullscreen ? 'h-screen w-screen bg-black flex items-center justify-center' : 'min-h-[calc(100vh-3.5rem)]'}`}>
        <div className={`${isFullscreen ? 'w-full h-full max-h-[56.25vw] max-w-[177.78vh]' : 'w-full h-full'} flex flex-col bg-gray-100`}>

        {layoutStyle === 'classic' ? (
          // ============ CLASSIC LAYOUT ============
          <>
            <div className="bg-white border-b border-gray-200 flex items-center justify-between flex-shrink-0 px-4 py-2">
              <img src="/images/TopsLogoCrop.png" alt="TOPS" className="h-12 w-auto" />
              <div className="text-right">
                <h1 className="font-bold text-gray-900 text-2xl">In-Store Specials</h1>
                <p className="text-gray-500 text-sm">Updated {currentTime.toLocaleDateString()} {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            </div>
            <div className="flex-1 flex flex-col overflow-hidden">
              {products.length > 0 ? (
                <>
                  {produceProducts.length > 0 && (
                    <div className="flex-1"><ClassicCategorySection title="Produce" products={produceProducts} columns={9} rows={2} /></div>
                  )}
                  {(groceryProducts.length > 0 || meatProducts.length > 0) && (
                    <div className="flex-1 flex">
                      <div className="w-1/2"><ClassicCategorySection title="Groceries" products={groceryProducts} columns={5} rows={2} /></div>
                      <div className="w-1/2"><ClassicCategorySection title="Meat" products={meatProducts} columns={5} rows={2} /></div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-3xl text-gray-400 mb-4">No products enabled for weekly display</p>
                    <p className="text-xl text-gray-400">Toggle the orange "Weekly" switch on products in the Admin panel.</p>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          // ============ FLYER LAYOUT ============
          <div className="h-full flex flex-col overflow-hidden">
            <PromoBanner isFullscreen={isFullscreen} />
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
              {products.length > 0 ? (
                <>
                  {/* Produce - Top Section: 9 cols x 2 rows */}
                  {produceProducts.length > 0 && (
                    <div className="min-h-0" style={{ flex: '2' }}>
                      <FlyerCategorySection title="Produce" products={produceProducts} columns={9} rows={2} />
                    </div>
                  )}
                  {/* Bottom Section: Groceries (left) and Meat (right) - 3 rows each */}
                  {(groceryProducts.length > 0 || meatProducts.length > 0) && (
                    <div className="flex min-h-0" style={{ flex: '3' }}>
                      <div className="w-1/2 h-full">
                        <FlyerCategorySection title="Groceries" products={groceryProducts} columns={5} rows={3} />
                      </div>
                      <div className="w-1/2 h-full">
                        <FlyerCategorySection title="Meat" products={meatProducts} columns={5} rows={3} />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-2xl text-gray-400 mb-4">No products enabled for flyer display</p>
                    <p className="text-lg text-gray-400">Toggle the orange "Weekly" switch on products in the Admin panel.</p>
                  </div>
                </div>
              )}
            </div>
            <div className="bg-red-700 text-white px-4 py-0.5 flex items-center justify-between flex-shrink-0 text-xs">
              <span>#183 - 1623 Centre St NW, Calgary, AB T2E 8S7</span>
              <span>Open 9am - 9pm Daily</span>
              <span>Prices valid while supplies last</span>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Exit Fullscreen Button */}
      {isFullscreen && (
        <Button onClick={toggleFullscreen} className="fixed top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 opacity-30 hover:opacity-100 transition-opacity" size="icon">
          <Minimize2 className="w-6 h-6" />
        </Button>
      )}
    </div>
  );
}
